<template>
    <div class="regicall-call-container">

        <!-- Loader -->
        <div class="loader-container"
            v-if="!loaded">
            <page-loader :loading="!loaded"></page-loader>
        </div>

        <!-- Warning for unsaved state -->
          <AlertDirty v-if="loaded"
                      v-bind:instances="[this.call.callComment, this.call.callAlert]"
                      dirtyFlag="$isDirty"
                      v-bind:alertText="navigationAlertText"
                      v-on:dirtyAlertAccepted="dirtyAlertAccepted"
                      v-on:dirtyAlertCanceled='$nprogress.done()'
          >
            <!-- <p slot="warning">{{navigationAlertText}}</p> -->
            <div slot="body">
              <p>{{navigationAlertText}}</p>
            </div>
            <div slot="footer" slot-scope="alertDirty">
              <button @click="alertDirty.accept" class="button is-danger">Verder</button>
              <button @click="alertDirty.cancel" class="button is-link">Terug</button>
            </div>
        </AlertDirty>

        <!-- Content  -->
        <div v-if="loaded"
             class="flex-row-reverse columns">

            <!-- Right column -->
            <div class="column">

                <!-- Client info -->
                <div class="box">
                    <ClientBio
                    :client="client"></ClientBio>
                </div>
            </div>

            <!-- Left column -->
            <div class="flex flex-col-reverse column is-two-thirds">

                <!-- Call history -->
                <div class="box">
                    <CallHistory :calls="callsHistory"
                                 :client="client"
                                 :count="historyCount"
                                 :loading="loadingHistory"
                                 :my-id="call.volunteer_id"
                                 @load-more="loadHistory"
                    >
                    </CallHistory>
                </div>

                <!-- Call manager -->
                <div class="box">
                    <ClientCall :call="call"
                                :numberOfCalls="numberOfCalls">
                    </ClientCall>
                </div>

                <!-- Sticky -->
                <div v-if="(call.client.sticky || call.client.about)" class="mb-6 box has-background-blue has-text-white">
                    <span v-if="!call.is_finished" class="has-text-size-20 has-text-weight-bold">Voordat je gaat bellen met
                        {{call.client.name}}</span>
                    <span class="is-pulled-right"><i class="fal fa-comment-exclamation fa-3x has-text-white opacity-60"></i></span>
                    <div class="my-1" v-if="call.client.sticky">{{ call.client.sticky }}</div>
                    <div class="my-1" v-if="call.client.about">{{ call.client.about }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
  import ClientCall from '../components/ClientCall';
  import CallHistory from '../components/CallHistory';
  import ClientBio from '../components/ClientBio';
  import AlertDirty from '../components/AlertDirty';
  import PageLoader from '../../../components/PageLoader';

  import { Call, STATUS as CALLSTATUS } from '../../../models/Call';
  import { Client } from '../../../models/Client';
  import { Volunteer } from '../../../models/Volunteer';
  import { Schedule } from '../../../models/Schedule';
  import { CallAttempt } from '../../../models/CallAttempt';
  import { CallComment } from '../../../models/CallComment';
  import { Period } from '../../../models/Period';
  import { CallAlert, CallAlertCategory } from '../../../models/CallAlert';
  import { Info as OverviewInfo } from '../containers/Overview';

  import { Promise } from 'q';

  import { startsWith } from 'lodash-es';
  import { serverDay, today } from '../../../datetime';

  export const Info = {
    name: 'Gepland gesprek',
    path: 'gesprek/:id',
    props: true,
    hideInNav: true
  };

  export default {
    Info,
    components: {
      ClientCall,
      CallHistory,
      ClientBio,
      AlertDirty,
      PageLoader
    },
    async created () {
      await this.loadData();
    },
    data () {
      return {
        title: Info.name,
        navigationAlertText: 'Er zijn aanpassingen in de notities die nog niet zijn opgeslagen. Weet je zeker dat je verder wilt?',
        loadingHistory: true,
        historyCount: null,
      };
    },
    computed: {
      callId () {
        return this.$route.params.id;
      },

      loaded () {
        return !!this.call && !!this.client && !!this.schedule && !!this.period;
      },

      numberOfCalls () {
        return Call.query()
            .where('status', CALLSTATUS.NO_ATTEMPTS)
            .where('date', value => serverDay(value) === today())
            .count();
      },

      call () {
        return Call.query()
          .withAll()
          .find(this.callId);
      },

      schedule () {
        // @todo - this query should somehow look up the schedule for this call id...
        return Schedule.query().withAllRecursive().where('active', true).first();
      },

      client () {
        return this.call.client;
      },

      callsHistory () {
          return Call.query()
            .withAll()
            .where(record => {
              return record.client_id === this.call.client_id &&
                  startsWith(record.status, 'completed') &&
                  record.id !== parseInt(this.callId);
            })
            .orderBy('id', 'desc')
            .get();
      },
      period () {
        return this.call.period;
      }
    },
    methods: {
      goToOverview () {
        this.$router.push({ name: OverviewInfo.name });
      },

      dirtyAlertAccepted () {
        // @todo reload data to get server version back?
      },
      async loadData () {
        this.$nprogress.start();

        // Add debug CallAlertCategories
        CallAlertCategory.insert({ data: { id: 1, status: 'something-off', name: 'Niet pluis-melding', description: 'Gebruik deze optie als je wilt dat er verder onderzoek gedaan wordt naar verborgen problemen.' }});
        CallAlertCategory.insert({ data: { id: 2, status: 'scheduling', name: 'Afwezigheid of wijziging dagdeel oudere' }});
        CallAlertCategory.insert({ data: { id: 3, status: 'feedback', name: 'Klacht of compliment' }});
        CallAlertCategory.insert({ data: { id: 4, status: 'other', name: 'Overig' }});

        this.$nprogress.inc();

        // Check if we have our schedule, else we must load that too
        if (!this.schedule) {
          await Schedule.loadActiveSchedule();
        }
        this.$nprogress.inc();

        // Load our main dependency
        const sc = await Call.$get({ params: { id: this.callId }});
        this.$nprogress.inc();

        // Load the most important bits
        await Promise.all([
          Period.$get({ params: { id:sc.period_id }}),
          Client.$get({ params: { id: sc.client_id }}),
          CallAttempt.$fetch({ query: { call_id: sc.id }})
        ]);
        this.$nprogress.inc();

        // Load the comment when detected
        if (sc.call_comment_id) {
          CallComment.$get({ params: { id: sc.call_comment_id }});
        }
        this.$nprogress.inc();


        // Load the supervisor note when detected
        if (sc.call_alert_id) {
          CallAlert.$get({ params: { id: sc.call_alert_id }});
        }
        this.$nprogress.done();

        await this.loadHistory();
        this.processedHistory = true;
      },
      async loadHistory () {
        this.loadingHistory = true;
        this.$nprogress.start();
        this.historyCount = await Call.historyCount({
          client_id: this.client.id
        });
        this.$nprogress.inc();

        const history = await Call.history({
          client_id: this.client.id,
          limit: 3,
          offset: this.callsHistory.length
        });
        this.$nprogress.inc();

        history.forEach(async (call) => {
          if (call.call_comment_id) {
            this.$nprogress.inc();
            await CallComment.$get({ params: { id: call.call_comment_id }});
          }
        });
        this.$nprogress.done();
        this.loadingHistory = false;
      },
    },
    beforeRouteUpdate (to, from, next) {
      next();
      this.loadData();
    }
  };
</script>
